/**
 * ChangeEmailFlow
 * ===============
 *
 * Single-screen flow for updating an informant's email address.
 *
 * Displays the current email, accepts a new email, validates it
 * client-side (format, uniqueness, not-same-as-current), and shows
 * an informational panel explaining the implications before the user
 * confirms.  On success, a confirmation screen is shown.
 *
 * Owns all form state internally so the parent doesn't need to track
 * email inputs, validation errors, or success flags.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';
import InformantFormInput from './InformantFormInput';
import {
  validateEmail,
  validateEmailNotDuplicate,
} from '../../utils/informantValidation';

/**
 * @param {Object}   props
 * @param {Object}   props.informant          – The informant whose email is changing
 * @param {Array}    props.allInformants       – All informants (for duplicate email check)
 * @param {Function} props.onChangeEmail       – (informantId, newEmail) => Promise
 * @param {Function} props.onBack              – Navigate back to the detail screen
 * @param {boolean}  props.actionLoading       – Whether the backend call is in progress
 * @param {string}   props.actionError         – Error message from the backend
 * @param {Function} props.clearActionState    – Reset actionError/actionSuccess
 * @param {Function} props.onFinish            – Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing         – Show spinner on the "Finish" button
 * @param {Object}   props.terminology         – { singular, lowerSingular }
 * @param {Function} props.sanitizeEmailInput  – Email input sanitizer
 */
export default function ChangeEmailFlow({
  informant,
  allInformants = [],
  onChangeEmail,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
  sanitizeEmailInput,
}) {
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleConfirm = useCallback(async () => {
    const trimmed = newEmail.trim().toLowerCase();
    const formatErr = validateEmail(trimmed);
    if (formatErr) { setEmailError(formatErr); return; }
    if (trimmed === informant?.email?.toLowerCase()) {
      setEmailError('New email must be different from the current email.');
      return;
    }
    const dupErr = validateEmailNotDuplicate(trimmed, allInformants);
    if (dupErr) { setEmailError(dupErr); return; }

    setEmailError('');
    try {
      await onChangeEmail(informant.id, trimmed);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onChangeEmail, informant, newEmail, allInformants]);

  // Derived: is the current input a valid, different email?
  const isEmailValid =
    !validateEmail(newEmail.trim()) &&
    newEmail.trim().toLowerCase() !== informant?.email?.toLowerCase();

  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline="Email address changed successfully!"
        detail={`A new welcome email has been sent to ${newEmail.trim()}. The ${terminology.lowerSingular} will need to set up their new account.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Change Email Address
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Update the email for{' '}
          <span className="font-medium">
            {informant.firstName} {informant.lastName}
          </span>
          .
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
        <span className="text-xs text-gray-600 uppercase tracking-wider font-semibold">
          Current email
        </span>
        <p className="text-xs font-medium text-gray-800 mt-0.5 break-all">
          {informant.email}
        </p>
      </div>

      <InformantFormInput
        label="New email address"
        type="email"
        value={newEmail}
        onChange={(e) => {
          setNewEmail(sanitizeEmailInput?.(e.target.value) ?? e.target.value);
          if (emailError) setEmailError('');
        }}
        error={emailError}
        required
        autoComplete="email"
      />

      {isEmailValid && (
        <WarningPanel variant="info">
          <p className="font-semibold mb-1">What this change means</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li className="break-all">
              A new account will be created for {newEmail.trim()}
            </li>
            <li className="break-all">
              The old account ({informant.email}) will be removed
            </li>
            <li>A welcome email will be sent to the new address</li>
            <li>
              They will be assigned to the same questionnaire{(informant.lifeStages || []).length > 1 ? 's' : ''}
            </li>
          </ul>
        </WarningPanel>
      )}

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={actionLoading || !isEmailValid}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading || !isEmailValid
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Updating…
            </span>
          ) : 'Confirm Email Change'}
        </button>
      </div>
    </div>
  );
}
